import {Component, computed, inject, input, signal, ChangeDetectionStrategy} from "@angular/core"
import {isInstanceOf, narrowInstance} from "@cm/utils"
import {MatListModule} from "@angular/material/list"
import {MatMenuModule} from "@angular/material/menu"
import {MatTooltipModule} from "@angular/material/tooltip"
import {HdriFilterInput, MaterialFilterInput, TemplateFilterInput} from "@generated"
import {AuthService} from "@app/common/services/auth/auth.service"
import {OrganizationsService} from "@app/common/services/organizations/organizations.service"
import {getNodeIconClass} from "@app/template-editor/helpers/template-icons"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {TemplateNodeClipboardService} from "@app/template-editor/services/template-node-clipboard.service"
import {
    ConfigGroup,
    ConfigVariant,
    getTemplateSwitchItemLabel,
    TemplateNode,
    NodeOwnerLikeNode,
    TemplateNodes,
    ObjectNode,
    SceneNodes,
    SwitchNode,
    TemplateListNode,
    isNodeOwnerLikeNode,
} from "@cm/template-nodes"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {TemplateMenuItemComponent} from "@template-editor/components/template-menu-item/template-menu-item.component"
import {TemplateMenuSectionComponent} from "@template-editor/components/template-menu-section/template-menu-section.component"
import {TemplateMenuComponent} from "@template-editor/components/template-menu/template-menu.component"
import {TemplateTreeComponent, TemplateTreeNode} from "@template-editor/components/template-tree/template-tree.component"
import {v4 as uuid4} from "uuid"
import {fundamentalTypes, type AnyFundamentalType} from "@cm/template-nodes"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-tree-add",
    templateUrl: "./template-tree-add.component.html",
    styleUrls: ["./template-tree-add.component.scss", "./../../helpers/template-icons.scss"],
    imports: [MatTooltipModule, TemplateMenuComponent, TemplateMenuSectionComponent, MatMenuModule, TemplateMenuItemComponent, MatListModule],
})
export class TemplateTreeAddComponent {
    readonly $treeNode = input.required<TemplateTreeNode<NodeOwnerLikeNode | SwitchNode>>({alias: "treeNode"})
    readonly $templateTree = input.required<TemplateTreeComponent>({alias: "templateTree"})
    private underCursor = false
    mode: "placeholder" | "buttons" | "menu" = "placeholder"
    public readonly authService = inject(AuthService)
    readonly organizations = inject(OrganizationsService)
    protected readonly permission = inject(PermissionsService)
    readonly clipboardService = inject(TemplateNodeClipboardService)
    $can = this.permission.$to

    hdriFilters: HdriFilterInput = {}
    templateFilters: TemplateFilterInput = {}
    materialFilters: MaterialFilterInput = {hasCyclesMaterial: true}
    getNodeIconClass = getNodeIconClass
    private readonly sceneManagerService = inject(SceneManagerService)
    readonly $hasSelection = computed(() => this.sceneManagerService.$selectedRootNodeParts().length > 0)
    readonly $node = computed(() => this.$treeNode().node)
    readonly $configGroupNode = computed(() => narrowInstance(this.$node(), ConfigGroup))
    readonly $switchNode = computed(() => narrowInstance(this.$node(), SwitchNode))
    readonly $nodeOwnerNode = computed(() => {
        const node = this.$node()
        if (isNodeOwnerLikeNode(node)) return node
        else return undefined
    })
    readonly $selectedNodes = computed(() => this.sceneManagerService.$selectedRootNodeParts().map((node) => node.templateNode))
    readonly $selectedPointGuideNodes = computed(() =>
        this.sceneManagerService
            .$selectedRootNodeParts()
            .map((templateNodePart) => this.sceneManagerService.getSceneNodes(templateNodePart.templateNode))
            .flat()
            .filter((x): x is SceneNodes.Mesh => SceneNodes.Mesh.is(x)),
    )
    readonly $selectedObjects = computed(() => this.$selectedNodes().filter(isInstanceOf(ObjectNode)))

    readonly switchTypes = Object.values(fundamentalTypes)

    readonly $getTemplateSwitchItemLabel = signal(getTemplateSwitchItemLabel)

    onMenuOpen() {
        this.mode = "menu"
        this.$templateTree().highlightNode(this.$treeNode(), "addMenu", true)
    }

    onMenuClose() {
        this.$templateTree().highlightNode(this.$treeNode(), "addMenu", false)
        this.mode = "buttons"
    }

    private addToConfigGroup(node: TemplateNode) {
        const configGroup = this.$configGroupNode()
        if (!configGroup) return

        this.sceneManagerService.modifyTemplateGraph(() => configGroup.parameters.nodes.addEntry(node))
    }

    addConfigVariant() {
        this.addToConfigGroup(new ConfigVariant({parameters: {name: "Config Variant", nodes: new TemplateNodes({parameters: {list: []}}), id: uuid4()}}))
    }

    addSwitch(entry: AnyFundamentalType) {
        this.addToConfigGroup(entry.createEmptySwitch(`${entry.name} Switch`))
    }

    private addReference<T extends TemplateNode>(nodes: TemplateListNode<T>, node: T) {
        if (nodes.entries.includes(node)) return
        nodes.addEntry(node)
    }

    addSelectionToSwitch() {
        const switchNode = this.$switchNode()
        if (!switchNode) return

        const otherSelectedNodes = this.$selectedNodes().filter((node) => node !== this.$node())

        if (otherSelectedNodes.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() => {
            for (const fundamentalType of Object.values(fundamentalTypes) as AnyFundamentalType[]) {
                if (switchNode instanceof fundamentalType.Switch) {
                    const nodes = switchNode.parameters.nodes as TemplateListNode<TemplateNode>
                    otherSelectedNodes.filter(fundamentalType.isLike).forEach((node) => this.addReference(nodes, node))
                    return
                }
            }
            throw new Error("Unsupported switch node type")
        })
    }
}
